/**
 * Form validation utilities for employee and other forms.
 * @module utils/formValidationUtils
 */

import countriesData from '../components/countriesData.json';

const phoneLengthRules = {
  AU: { min: 9, max: 9 },
  AE: { min: 9, max: 9 },
  CA: { min: 10, max: 10 },
  DE: { min: 10, max: 11 },
  FR: { min: 9, max: 9 },
  GB: { min: 10, max: 11 },
  IE: { min: 9, max: 10 },
  IN: { min: 10, max: 10 },
  LK: { min: 9, max: 9 },
  MY: { min: 9, max: 10 },
  NZ: { min: 8, max: 9 },
  PK: { min: 10, max: 10 },
  SG: { min: 8, max: 8 },
  US: { min: 10, max: 10 },
  ZA: { min: 9, max: 9 },
};

const dialCodeList = [...countriesData]
  .map((country) => country.dial_code)
  .sort((first, second) => second.length - first.length);

export const getPhoneCountryFromValue = (phone) => {
  const normalizedValue = String(phone ?? '').replace(/\s+/g, '');
  if (!normalizedValue) return null;

  return countriesData.find((country) => normalizedValue.startsWith(country.dial_code)) || null;
};

export const getNationalPhoneDigits = (phone) => {
  const normalizedDigits = String(phone ?? '').replace(/\D/g, '');
  if (!normalizedDigits) return '';

  const matchingDialCode = dialCodeList.find((dialCode) => {
    const dialDigits = dialCode.replace(/\D/g, '');
    return normalizedDigits.startsWith(dialDigits);
  });

  if (!matchingDialCode) {
    return normalizedDigits;
  }

  const dialDigits = matchingDialCode.replace(/\D/g, '');
  return normalizedDigits.slice(dialDigits.length);
};

export const getPhoneLengthRule = (countryCode) => {
  if (!countryCode) return { min: 9, max: 15 };
  return phoneLengthRules[String(countryCode).toUpperCase()] || { min: 9, max: 15 };
};

export const getPhoneInputDigitLimit = (countryCode) => {
  if (!countryCode) return 15;

  const normalizedCountryCode = String(countryCode).toUpperCase();
  if (normalizedCountryCode === 'AU') {
    return 10;
  }

  return getPhoneLengthRule(normalizedCountryCode).max;
};

export const validatePhoneNumber = (
  value,
  {
    countryCode,
    requiredMessage = 'Please enter a mobile number.',
    invalidMessage = 'Please enter a valid mobile number.',
  } = {},
) => {
  const phone = String(value ?? '').trim();
  if (!phone) return requiredMessage;

  const detectedCountry = getPhoneCountryFromValue(phone);
  const normalizedCountryCode = String(countryCode || detectedCountry?.code || '').trim().toUpperCase();
  const phoneDigits = getNationalPhoneDigits(phone);
  const { min, max } = getPhoneLengthRule(normalizedCountryCode);
  const countryName = countriesData.find((country) => country.code === normalizedCountryCode)?.name;

  if (phoneDigits.length < min || phoneDigits.length > max) {
    return countryName
      ? `Please enter a valid ${countryName} mobile number.`
      : invalidMessage;
  }

  return '';
};

/**
 * Validate employee form fields with comprehensive validation rules.
 * Checks for required fields, format validation, and business rules.
 * 
 * @param {Object} fields - The form fields to validate
 * @param {string} fields.firstName - Employee first name (required, letters only)
 * @param {string} [fields.middleName] - Employee middle name (optional, letters only if provided)
 * @param {string} fields.lastName - Employee last name (required, letters only)
 * @param {string} fields.phone - Mobile number (required, normalized international format)
 * @param {string} fields.email - Email address (required, valid email format, max 254 chars)
 * @param {string} fields.jobRole - Job role (required)
 * @param {string} fields.employmentType - Employment type (required)
 * @returns {Object} errors - Key-value pairs of field errors (empty object if valid)
 * 
 * @example
 * const fields = {
 *   firstName: 'John',
 *   lastName: 'Doe',
 *   phone: '123456789',
 *   email: 'john@example.com',
 *   jobRole: 'Developer',
 *   employmentType: 'Full-time'
 * };
 * const errors = validateEmployeeForm(fields);
 * if (Object.keys(errors).length > 0) {
 *   // Handle validation errors
 * }
 */
export function validateEmployeeForm(fields) {
  const errors = {};
  const nameRegex = /^[A-Za-z][A-Za-z'\-\s]*$/;

  const firstName = (fields.firstName || '').trim();
  const middleName = (fields.middleName || '').trim();
  const lastName = (fields.lastName || '').trim();
  const phone = (fields.phone || '').trim();
  const email = (fields.email || '').trim();
  const jobRole = (fields.jobRole || '').trim();
  const employmentType = (fields.employmentType || '').trim();

  if (!firstName) errors.firstName = 'Please enter a first name.';
  else if (!nameRegex.test(firstName)) errors.firstName = 'First name can include letters, spaces, apostrophes, and hyphens only.';

  if (middleName && !nameRegex.test(middleName)) errors.middleName = 'Middle name can include letters, spaces, apostrophes, and hyphens only.';

  if (!lastName) errors.lastName = 'Please enter a last name.';
  else if (!nameRegex.test(lastName)) errors.lastName = 'Last name can include letters, spaces, apostrophes, and hyphens only.';

  const phoneError = validatePhoneNumber(phone, { countryCode: fields.phoneCountryCode });
  if (phoneError) errors.phone = phoneError;

  const emailError = validateEmailAddress(email);
  if (emailError) errors.email = emailError;

  if (!jobRole) errors.jobRole = 'Please select a job role.';
  if (!employmentType) errors.employmentType = 'Please select an employment type.';

  return errors;
}

export const isBlank = (value) => String(value ?? '').trim() === '';

export const validateRequiredFields = (values, labels) => {
  const errors = {};

  Object.keys(labels).forEach((key) => {
    if (isBlank(values[key])) {
      errors[key] = `${labels[key]} is required.`;
    }
  });

  return errors;
};

export const validateEmailAddress = (
  value,
  {
    requiredMessage = 'Please enter an email address.',
    invalidMessage = 'Please enter a valid email address, for example name@company.com.',
    maxLengthMessage = 'Email address is too long.',
  } = {},
) => {
  const email = String(value ?? '').trim();

  if (!email) {
    return requiredMessage;
  }

  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
    return invalidMessage;
  }

  if (email.length > 254) {
    return maxLengthMessage;
  }

  return '';
};

export const validateEmailOrPhone = (value) => {
  const trimmed = String(value ?? '').trim();
  if (!trimmed) {
    return 'Please enter your email address or phone number.';
  }

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const phoneRegex = /^\+?[0-9\s()-]{8,20}$/;

  if (emailRegex.test(trimmed) || phoneRegex.test(trimmed)) {
    return '';
  }

  return 'Enter a valid email address or phone number.';
};

export const validatePasswordStrength = (password) => {
  const value = String(password ?? '');
  if (value.length < 8) {
    return 'Password must be at least 8 characters long.';
  }
  if (!/[A-Za-z]/.test(value) || !/[0-9]/.test(value)) {
    return 'Password must include at least one letter and one number.';
  }

  return '';
};

export const getFirstErrorMessage = (errors) => {
  if (!errors || typeof errors !== 'object') return '';
  const firstKey = Object.keys(errors)[0];
  return firstKey ? errors[firstKey] : '';
};
